import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  TextField,
  Typography,
  Avatar,
  Button,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import BusinessIcon from '@mui/icons-material/Business';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import arivuIconLogo from '../../assets/logo/ArivuEN.png';
import arivuBotLogo from '../../assets/logo/ArivuBot.png';
import { postWithToken, useAuth } from '../../authentication/AuthProvider';
import { apiUrl } from '../../config';
import './ArivuBot.css';

// ─── Confidence-boosting processing messages ─────────────────────────────────
const PROCESSING_STEPS = [
  'Scanning your document...',
  'Identifying materials and quantities...',
  'Cross-referencing Arivu material library...',
  'Matching emission factors...',
  'Calculating lifecycle data points...',
  'Preparing your BOM summary...',
];

const RETRY_OPTIONS = [
  { type: 'file_upload', label: 'Upload BOM File', description: 'Try uploading a different file or format' },
  { value: 'describe', label: 'Describe Materials', description: 'Describe your product or project manually' }
];

// ─── Processing Overlay Component ────────────────────────────────────────────
const ProcessingOverlay = ({ visible, stepIndex }) => {
  if (!visible) return null;
  const safeIndex = Math.min(stepIndex, PROCESSING_STEPS.length - 1);
  
  // Cap automated progress bar at 95% so it never claims 100% until the API returns
  const progress = Math.min(
    Math.round(((safeIndex + 1) / PROCESSING_STEPS.length) * 100),
    95
  );

  return (
    <Box className="arivu-processing-overlay">
      <Box className="arivu-processing-card">
        <Box className="arivu-processing-icon-wrap">
          <AutoAwesomeIcon sx={{ fontSize: 32, color: '#8eb33a' }} />
          <Box className="arivu-processing-pulse" />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a3326', mb: 0.5 }}>
          Arivu is thinking...
        </Typography>
        <Typography variant="body2" sx={{ color: '#4a7a5f', mb: 2, minHeight: 20, textAlign: 'center' }}>
          {PROCESSING_STEPS[safeIndex]}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            width: '100%',
            height: 6,
            borderRadius: 3,
            backgroundColor: '#d4e9c3',
            '& .MuiLinearProgress-bar': { backgroundColor: '#8eb33a', borderRadius: 3 },
          }}
        />
        <Typography variant="caption" sx={{ color: '#888', mt: 1 }}>
          {progress}% complete
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Main ArivuBot Component ──────────────────────────────────────────────────
const ArivuBot = () => {
  const { isArivuChatEnabled } = useAuth();
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Persist chat drawer state across hard page refreshes/redirects
  const [isOpen, setIsOpen] = useState(() => {
    return sessionStorage.getItem('arivu_chat_isOpen') === 'true';
  });
  
  const [messages, setMessages] = useState(() => {
    try {
      const saved = sessionStorage.getItem('arivu_chat_messages');
      return saved ? JSON.parse(saved) : [];
    } catch (_) {
      return [];
    }
  });
  
  const [chatState, setChatState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('arivu_chat_state');
      return saved ? JSON.parse(saved) : null;
    } catch (_) {
      return null;
    }
  });
  
  const [hasInitialized, setHasInitialized] = useState(() => {
    return sessionStorage.getItem('arivu_chat_hasInitialized') === 'true';
  });

  const [selectedGeography, setSelectedGeography] = useState(() => {
    return sessionStorage.getItem('arivu_chat_selectedGeography') || 'Global Average';
  });

  const [isCustomGeography, setIsCustomGeography] = useState(() => {
    const saved = sessionStorage.getItem('arivu_chat_selectedGeography') || 'Global Average';
    const presets = ['Global Average', 'Europe', 'North America', 'India', 'Germany', 'United States', 'China'];
    return !presets.includes(saved);
  });

  const [customGeographyInput, setCustomGeographyInput] = useState(() => {
    const saved = sessionStorage.getItem('arivu_chat_selectedGeography') || 'Global Average';
    const presets = ['Global Average', 'Europe', 'North America', 'India', 'Germany', 'United States', 'China'];
    return presets.includes(saved) ? '' : saved;
  });

  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Processing overlay state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStepIndex, setProcessingStepIndex] = useState(0);
  const processingIntervalRef = useRef(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Scroll ──────────────────────────────────────────────────────────────────
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // ── Processing overlay animation ────────────────────────────────────────────
  const startProcessingAnimation = () => {
    setIsProcessing(true);
    setProcessingStepIndex(0);
    let step = 0;
    processingIntervalRef.current = setInterval(() => {
      step += 1;
      if (step < PROCESSING_STEPS.length) {
        setProcessingStepIndex(step);
      }
    }, 3500); // 3.5 seconds per step gives a realistic progress simulation for mapping
  };

  const stopProcessingAnimation = () => {
    clearInterval(processingIntervalRef.current);
    setIsProcessing(false);
    setProcessingStepIndex(0);
  };

  useEffect(() => {
    return () => clearInterval(processingIntervalRef.current);
  }, []);

  // ── Drawer toggle ────────────────────────────────────────────────────────────
  const toggleDrawer = (open) => (event) => {
    if (event?.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) return;
    setIsOpen(open);
  };

  // ── Initialize conversation on first open ────────────────────────────────────
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      setHasInitialized(true);
      fetchInitialMessage();
    }
  }, [isOpen, hasInitialized]);

  // Synchronize state changes to sessionStorage to recover after redirects
  useEffect(() => {
    sessionStorage.setItem('arivu_chat_isOpen', isOpen);
  }, [isOpen]);

  useEffect(() => {
    sessionStorage.setItem('arivu_chat_messages', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    sessionStorage.setItem('arivu_chat_state', JSON.stringify(chatState));
  }, [chatState]);

  useEffect(() => {
    sessionStorage.setItem('arivu_chat_hasInitialized', hasInitialized);
  }, [hasInitialized]);

  useEffect(() => {
    sessionStorage.setItem('arivu_chat_selectedGeography', selectedGeography);
  }, [selectedGeography]);

  // Hide the chatbot FAB and drawer completely on Auth/Landing pages
  const isAuthPage =
    location.pathname.includes('/login') ||
    location.pathname.includes('/register') ||
    location.pathname === '/';

  if (isAuthPage) return null;

  const renderMessageText = (text) => {
    if (!text) return null;
    // Regex splits on both **bold** tags and [label](url) link tags
    const tokenRegex = /(\*\*.*?\*\*|\[.*?\]\(.*?\))/g;
    const parts = text.split(tokenRegex);
    
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.slice(2, -2);
        return <strong key={index} style={{ fontWeight: 700 }}>{boldText}</strong>;
      }
      if (part.startsWith('[') && part.includes('](') && part.endsWith(')')) {
        const closeBracketIdx = part.indexOf('](');
        const label = part.slice(1, closeBracketIdx);
        const url = part.slice(closeBracketIdx + 2, -1);
        
        // Determine if it's an internal route link
        const isInternal = url.startsWith('/') || url.startsWith(window.location.origin) || (!url.includes('://') && !url.startsWith('mailto:') && !url.startsWith('tel:'));
        
        if (isInternal) {
          // Normalize the path by removing origin if fully qualified but pointing to this site
          const toPath = url.startsWith(window.location.origin)
            ? url.slice(window.location.origin.length)
            : url;
            
          return (
            <Link
              key={index}
              to={toPath}
              style={{ color: '#8eb33a', fontWeight: 600, textDecoration: 'underline' }}
            >
              {label}
            </Link>
          );
        }
        
        return (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#8eb33a', fontWeight: 600, textDecoration: 'underline' }}
          >
            {label}
          </a>
        );
      }
      return part;
    });
  };

  const fetchInitialMessage = async () => {
    // Show instant, premium welcome greeting so the screen isn't blank
    setMessages([
      {
        id: 1,
        sender: 'bot',
        text: "Welcome to **Arivu AI**! I'm here to help you build your **LCA template** easily. Let's get started!",
        ui_options: [],
      },
    ]);
    setIsLoading(true);
    try {
      const response = await postWithToken(`${apiUrl}/arivu_chat/message`, {
        user_input: '',
        state: {},
      });
      const data = await response.json();
      if (data.state) setChatState(data.state);
      if (data.message) {
        setMessages([
          {
            id: 1,
            sender: 'bot',
            text: data.message,
            ui_options: data.ui_options || [],
          },
        ]);
      }
    } catch (error) {
      console.error('[ArivuBot] Init error:', error);
      setMessages([
        {
          id: 1,
          sender: 'bot',
          text: "Hello! I'm Arivu AI. I'm having trouble connecting right now — please try again in a moment.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = () => {
    sessionStorage.removeItem('arivu_chat_state');
    sessionStorage.removeItem('arivu_chat_messages');
    sessionStorage.removeItem('arivu_chat_hasInitialized');
    setChatState(null);
    setMessages([]);
    setHasInitialized(true);
    fetchInitialMessage();
  };

  // ── Core: send message to backend state machine ──────────────────────────────
  const sendToBackend = async (text, extra = {}, showProcessing = false, overrideState = null) => {
    if (showProcessing) startProcessingAnimation();
    else setIsLoading(true);

    try {
      const response = await postWithToken(`${apiUrl}/arivu_chat/message`, {
        user_input: text,
        state: overrideState || chatState || {},
        extra,
      });
      const data = await response.json();
      if (data.state) setChatState(data.state);

      if (data.message) {
        setMessages((prev) => [
          ...prev,
          {
            id: prev.length + 1,
            sender: 'bot',
            text: data.message,
            ui_options: data.ui_options || [],
          },
        ]);
      }

      // Auto-trigger: BOM matching (describe path)
      const bomTrigger = data.ui_options?.find((o) => o.type === 'bom_matching_trigger');
      if (bomTrigger) {
        await handleBomMatching(bomTrigger.raw_items, data.state);
        return;
      }

      // Auto-trigger: generate template after confirm
      const creatingTrigger = data.ui_options?.find((o) => o.type === 'creating');
      if (creatingTrigger) {
        await handleGenerateTemplate(data.state);
      }
    } catch (error) {
      console.error('[ArivuBot] sendToBackend error:', error);
      setMessages((prev) => [
        ...prev,
        { id: prev.length + 1, sender: 'bot', text: 'Something went wrong. Please try again.' },
      ]);
    } finally {
      if (showProcessing) stopProcessingAnimation();
      else setIsLoading(false);
    }
  };

  // ── User text input ──────────────────────────────────────────────────────────
  const handleSendMessage = () => {
    const text = inputValue.trim();
    if (!text) return;
    setMessages((prev) => [...prev, { id: prev.length + 1, sender: 'user', text }]);
    setInputValue('');
    sendToBackend(text);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) handleSendMessage();
  };

  const handleGeographyChange = (val) => {
    if (val === 'custom') {
      setIsCustomGeography(true);
      setSelectedGeography(customGeographyInput.trim() || 'Global Average');
    } else {
      setIsCustomGeography(false);
      setSelectedGeography(val);
    }
  };

  const handleCustomGeographyInput = (val) => {
    setCustomGeographyInput(val);
    setSelectedGeography(val.trim() || 'Global Average');
  };

  // ── Option button click ──────────────────────────────────────────────────────
  const handleOptionClick = (option) => {
    // File upload trigger
    if (option.type === 'file_upload') {
      fileInputRef.current?.click();
      return;
    }

    // BOM review confirm
    if (option.type === 'bom_review') {
      setMessages((prev) => [
        ...prev,
        { id: prev.length + 1, sender: 'user', text: 'Materials confirmed — proceed.' },
      ]);
      sendToBackend('Materials confirmed — proceed.', {
        confirmed_items: option.items,
      });
      return;
    }

    // Standard option buttons (product/project, upload/describe, confirm/edit)
    setMessages((prev) => [...prev, { id: prev.length + 1, sender: 'user', text: option.label }]);

    const extra = {};
    if (option.value === 'product' || option.value === 'project') extra.entity_type = option.value;
    if (option.value === 'upload' || option.value === 'describe') extra.bom_input_mode = option.value;
    if (option.value === 'confirm_create' || option.value === 'edit') extra.action = option.value;

    sendToBackend(option.label, extra);
  };

  // ── File upload — uses /bom/upload_bom (proven, no-auth streaming endpoint) ─
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null; // Reset input

    setMessages((prev) => [
      ...prev,
      { id: prev.length + 1, sender: 'user', text: `📎 Uploading: ${file.name}` },
    ]);

    startProcessingAnimation();

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('geography', selectedGeography);

      // Use /bom/upload_bom which is an open streaming endpoint — no JWT issues
      const uploadRes = await fetch(`${apiUrl}/bom/upload_bom`, {
        method: 'POST',
        body: formData,
      });

      if (!uploadRes.ok) {
        stopProcessingAnimation();
        setMessages((prev) => [
          ...prev,
          {
            id: prev.length + 1,
            sender: 'bot',
            text: `❌ Upload failed (${uploadRes.status}). Please try a CSV or Excel file.`,
            ui_options: RETRY_OPTIONS,
          },
        ]);
        return;
      }

      // Parse NDJSON streaming response
      const text = await uploadRes.text();
      const lines = text.trim().split('\n').filter(Boolean);
      let hydrated = [];

      for (const line of lines) {
        try {
          const parsed = JSON.parse(line);
          if (parsed.status === 'ping') {
            continue;
          }
          if (parsed.status === 'complete' && parsed.data?.items) {
            hydrated = parsed.data.items;
          }
        } catch (_) { /* ignore malformed lines */ }
      }

      stopProcessingAnimation();

      if (hydrated.length === 0) {
        setMessages((prev) => [
          ...prev,
          {
            id: prev.length + 1,
            sender: 'bot',
            text: `❌ Could not extract materials from the file. Please try a different CSV or Excel format.`,
            ui_options: RETRY_OPTIONS,
          },
        ]);
        return;
      }

      // Feed the hydrated items into the Arivu conversation state machine
      await sendToBackend('', { hydrated_items: hydrated });
    } catch (err) {
      stopProcessingAnimation();
      console.error('[ArivuBot] Upload error:', err);
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          sender: 'bot',
          text: '❌ Upload failed. Please check your connection and try again.',
          ui_options: RETRY_OPTIONS,
        },
      ]);
    }
  };

  // ── BOM matching (describe path) ─────────────────────────────────────────────
  const handleBomMatching = async (rawItems, currentState) => {
    startProcessingAnimation();
    try {
      const res = await postWithToken(`${apiUrl}/arivu_chat/map_bom`, { 
        raw_items: rawItems,
        geography: selectedGeography
      });
      const data = await res.json();
      stopProcessingAnimation();
      await sendToBackend('', { hydrated_items: data.items || [] }, false, currentState);
    } catch (err) {
      stopProcessingAnimation();
      console.error('[ArivuBot] BOM matching error:', err);
    }
  };

  // ── Generate template ────────────────────────────────────────────────────────
  const handleGenerateTemplate = async (currentState) => {
    setIsLoading(true);
    try {
      const res = await postWithToken(`${apiUrl}/arivu_chat/generate_template`, { state: currentState });
      const data = await res.json();

      if (data.success && data.workspace_url) {
        setMessages((prev) => [
          ...prev,
          {
            id: prev.length + 1,
            sender: 'bot',
            text: `✅ **Template created successfully!**\n\nI am redirecting you to your workspace now. If you are not redirected automatically, [click here to open the workspace](${data.workspace_url}).`,
            workspace_url: data.workspace_url,
          },
        ]);
        setTimeout(() => { navigate(data.workspace_url); }, 2500);
      } else {
        setMessages((prev) => [
          ...prev,
          { id: prev.length + 1, sender: 'bot', text: `Failed to create template: ${data.error || 'Unknown error'}` },
        ]);
      }
    } catch (err) {
      console.error('[ArivuBot] Generate template error:', err);
      setMessages((prev) => [
        ...prev,
        { id: prev.length + 1, sender: 'bot', text: 'Error creating template. Please try again.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Render option chips / cards ───────────────────────────────────────────────
  const renderOptions = (options) => {
    if (!options || options.length === 0) return null;
    const visible = options.filter(
      (o) => o.type !== 'bom_matching_trigger' && o.type !== 'creating'
    );
    if (visible.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5, ml: 5 }}>
        {visible.map((opt, i) => {
          // BOM review card with material list
          if (opt.type === 'bom_review' && opt.items) {
            return (
              <Box key={i} className="arivu-bom-review-card">
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1a3326', display: 'block', mb: 1 }}>
                  📦 {opt.items.length} Materials Found
                </Typography>
                <Box sx={{ maxHeight: 120, overflowY: 'auto', mb: 1 }}>
                  {opt.items.map((item, j) => (
                    <Typography key={j} variant="caption" sx={{ display: 'block', color: '#4a5568', lineHeight: 1.6 }}>
                      • {item.matched_name || item.raw_name}
                      {item.quantity ? ` — ${item.quantity} ${item.unit || ''}` : ''}
                    </Typography>
                  ))}
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  sx={{
                    backgroundColor: '#659d33',
                    color: '#fff',
                    textTransform: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: '#061d15' },
                  }}
                  onClick={() => handleOptionClick(opt)}
                >
                  ✅ Confirm Materials
                </Button>
              </Box>
            );
          }

          // Standard option buttons (text-only inside chat flow)
          return (
            <Button
              key={i}
              variant="outlined"
              size="small"
              onClick={() => handleOptionClick(opt)}
              startIcon={null}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                borderRadius: '12px',
                textTransform: 'none',
                borderColor: '#659d33',
                color: '#061d15',
                padding: '8px 14px',
                '&:hover': { backgroundColor: '#f8fbfa', borderColor: '#061d15' },
              }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                  {opt.label || 'Select'}
                </Typography>
                {opt.description && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', lineHeight: 1.4 }}>
                    {opt.description}
                  </Typography>
                )}
              </Box>
            </Button>
          );
        })}
      </Box>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  if (!isArivuChatEnabled) {
    return null;
  }

  return (
    <>
      {/* Floating Action Button */}
      <div
        className={`arivu-bot-fab ${isOpen ? 'hidden' : ''}`}
        onClick={toggleDrawer(true)}
        title="Open Arivu AI"
      >
        <img src={arivuBotLogo} alt="Arivu AI" className="arivu-bot-fab-icon" />
      </div>

      {/* Chat Drawer */}
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={toggleDrawer(false)}
        classes={{ paper: 'arivu-bot-drawer' }}
        hideBackdrop
        variant="persistent"
        sx={{ zIndex: 9999 }}
      >
        <Box className="arivu-bot-container">

          {/* Header */}
          <Box className="arivu-bot-header">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src={arivuIconLogo} alt="Arivu" style={{ height: 28, width: 'auto' }} />
              <Box component="span" sx={{ color: '#8eb33a', fontWeight: 'bold', mx: 0.5, fontSize: '1.2rem' }}>|</Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff', fontSize: '1rem' }}>
                Arivu AI
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                onClick={handleNewChat}
                size="small"
                title="New Chat Session"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  mr: 0.5,
                  '&:hover': {
                    color: '#8eb33a',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={toggleDrawer(false)} size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Sourcing Region Selector Bar */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: '8px 16px',
            backgroundColor: '#f8fbfa',
            borderBottom: '2px solid #659d33',
            gap: 1
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              gap: 1
            }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#061d15', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span style={{ fontSize: '0.85rem' }}>🌍</span> GWP Sourcing Region:
              </Typography>
              <select
                value={isCustomGeography ? 'custom' : selectedGeography}
                onChange={(e) => handleGeographyChange(e.target.value)}
                style={{
                  padding: '4px 24px 4px 10px',
                  borderRadius: '8px',
                  border: '1.5px solid #659d33',
                  backgroundColor: '#fff',
                  color: '#061d15',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  outline: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  appearance: 'none',
                  backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23659d33\' stroke-width=\'2.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                  backgroundSize: '12px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                }}
              >
                <option value="Global Average">Global Average</option>
                <option value="Europe">Europe</option>
                <option value="North America">North America</option>
                <option value="India">India</option>
                <option value="Germany">Germany</option>
                <option value="United States">United States</option>
                <option value="China">China</option>
                <option value="custom">Other Region...</option>
              </select>
            </Box>

            {isCustomGeography && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                mt: 0.5,
                animation: 'slideDown 0.2s ease-out',
                '@keyframes slideDown': {
                  'from': { opacity: 0, transform: 'translateY(-5px)' },
                  'to': { opacity: 1, transform: 'translateY(0)' }
                }
              }}>
                <input
                  type="text"
                  placeholder="Enter custom region (e.g. Vietnam, Brazil)..."
                  value={customGeographyInput}
                  onChange={(e) => handleCustomGeographyInput(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1.5px solid #659d33',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    outline: 'none',
                    color: '#061d15',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Messages Area */}
          <Box className="arivu-bot-messages" sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>

            {(!chatState || chatState.step === 'welcome') ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flexGrow: 1,
                padding: '24px',
                textAlign: 'center',
                animation: 'fadeIn 0.6s ease-out',
                '@keyframes fadeIn': {
                  'from': { opacity: 0, transform: 'translateY(15px)' },
                  'to': { opacity: 1, transform: 'translateY(0)' }
                }
              }}>
                {/* Centered Circular Logo with elegant pulsing glow */}
                <Box sx={{ position: 'relative', mb: 3 }}>
                  <Avatar
                    src={arivuBotLogo}
                    sx={{
                      width: 90,
                      height: 90,
                      backgroundColor: 'transparent',
                      '& img': { objectFit: 'contain', padding: '14px' },
                      border: '3px solid #659d33',
                      boxShadow: '0 8px 24px rgba(101, 157, 51, 0.25)',
                      animation: 'pulseGlow 2.5s infinite ease-in-out',
                      '@keyframes pulseGlow': {
                        '0%, 100%': { boxShadow: '0 8px 24px rgba(101, 157, 51, 0.25)', transform: 'scale(1)' },
                        '50%': { boxShadow: '0 12px 32px rgba(101, 157, 51, 0.45)', transform: 'scale(1.03)' }
                      }
                    }}
                  />
                </Box>

                {/* Animated Brand Title */}
                <Typography variant="h5" sx={{
                  fontWeight: 800,
                  color: '#061d15',
                  mb: 1,
                  letterSpacing: '0.5px',
                  fontFamily: '"Outfit", "Inter", sans-serif',
                }}>
                  Arivu AI
                </Typography>

                {/* Animated Welcome Message */}
                <Typography variant="body2" sx={{
                  color: '#4b4b4b',
                  mb: 4,
                  maxWidth: '280px',
                  lineHeight: 1.6,
                  fontSize: '0.875rem'
                }}>
                  Welcome to Arivu AI! Let's build your LCA template. Select a template type below to begin your A1–A5 Life Cycle Assessment.
                </Typography>

                {/* Centered Premium Action Cards */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  width: '100%',
                  maxWidth: '340px'
                }}>
                  <Button
                    onClick={() => handleOptionClick({ label: 'Create Product Template', value: 'product' })}
                    variant="outlined"
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      padding: '16px 20px',
                      borderRadius: '16px',
                      border: '1.5px solid #cbd5e1',
                      color: '#061d15',
                      backgroundColor: '#ffffff',
                      textTransform: 'none',
                      textAlign: 'left',
                      width: '100%',
                      gap: 2,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        borderColor: '#659d33',
                        backgroundColor: '#f8fbfa',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 8px 24px rgba(101, 157, 51, 0.12)',
                        '& .MuiSvgIcon-root': {
                          color: '#061d15'
                        }
                      }
                    }}
                  >
                    <Inventory2Icon sx={{ fontSize: 32, color: '#659d33', flexShrink: 0, transition: 'color 0.2s' }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5, fontSize: '0.95rem', color: '#061d15', textAlign: 'left' }}>
                        Create Product Template
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#4b4b4b', fontSize: '0.75rem', textAlign: 'left', lineHeight: 1.4 }}>
                        A1–A3 stages: Raw materials, Transport, and Manufacturing processes.
                      </Typography>
                    </Box>
                  </Button>

                  <Button
                    onClick={() => handleOptionClick({ label: 'Create Project Template', value: 'project' })}
                    variant="outlined"
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      padding: '16px 20px',
                      borderRadius: '16px',
                      border: '1.5px solid #cbd5e1',
                      color: '#061d15',
                      backgroundColor: '#ffffff',
                      textTransform: 'none',
                      textAlign: 'left',
                      width: '100%',
                      gap: 2,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        borderColor: '#659d33',
                        backgroundColor: '#f8fbfa',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 8px 24px rgba(101, 157, 51, 0.12)',
                        '& .MuiSvgIcon-root': {
                          color: '#061d15'
                        }
                      }
                    }}
                  >
                    <BusinessIcon sx={{ fontSize: 32, color: '#659d33', flexShrink: 0, transition: 'color 0.2s' }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5, fontSize: '0.95rem', color: '#061d15', textAlign: 'left' }}>
                        Create Project Template
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#4b4b4b', fontSize: '0.75rem', textAlign: 'left', lineHeight: 1.4 }}>
                        A1–A5 stages: Includes on-site Transport, Construction, and energy.
                      </Typography>
                    </Box>
                  </Button>
                </Box>
              </Box>
            ) : (
              messages.map((msg, idx) => (
                <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', mb: 1.5 }}>
                  <Box className={`arivu-bot-message-wrapper ${msg.sender === 'user' ? 'user' : 'bot'}`}>
                    {msg.sender === 'bot' && (
                      <Avatar
                        src={arivuBotLogo}
                        sx={{ width: 24, height: 24, mr: 1, flexShrink: 0, backgroundColor: 'transparent', '& img': { objectFit: 'contain' } }}
                      />
                    )}
                    <Box className={`arivu-bot-bubble ${msg.sender === 'user' ? 'user' : 'bot'}`}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {renderMessageText(msg.text)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Render option chips below bot messages */}
                  {msg.sender === 'bot' && renderOptions(msg.ui_options)}
                </Box>
              ))
            )}

            {/* Processing Overlay in standard scroll flow */}
            <ProcessingOverlay visible={isProcessing} stepIndex={processingStepIndex} />

            {/* Simple typing indicator (used for non-file-processing loading) */}
            {isLoading && !isProcessing && (
              <Box className="arivu-bot-message-wrapper bot">
                <Avatar src={arivuBotLogo} sx={{ width: 24, height: 24, mr: 1, flexShrink: 0, backgroundColor: 'transparent', '& img': { objectFit: 'contain' } }} />
                <Box className="arivu-bot-bubble bot typing">
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                </Box>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box className="arivu-bot-input-area">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Ask Arivu AI..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              className="arivu-bot-input"
              disabled={isLoading || isProcessing}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || isLoading || isProcessing}
                    className="arivu-bot-send-btn"
                  >
                    <SendIcon />
                  </IconButton>
                ),
              }}
            />
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept=".pdf,.csv,.xlsx,.xls,.txt"
              onChange={handleFileUpload}
            />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default ArivuBot;
